import { get as _get, pick as _pick } from 'lodash-es';
import { useCallback, useMemo } from 'react';
import { useMutation } from '@apollo/client/react';
import type { Group } from '../types/Group';
import type { Event } from '../types/Event';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import { makeStyles } from 'tss-react/mui';
import { uploadImagesMutation } from '../apollo/image-uploads';
import FormSavingBar from '../components/forms/form-saving-bar';
import LicenseField from '../components/forms/license-field';
import DropzoneFileField from '../components/forms/dropzone-file-field';
import { useAppContext } from '../contexts/app';
import { meQuery } from '../apollo/auth';

const useStyles = makeStyles()((theme) => ({
  paper: {
    padding: theme.spacing(2),
    margin: theme.spacing(2, 0),
    backgroundColor: theme.palette.secondary.background,
  },
  dropzone: {
    marginBottom: theme.spacing(2),
  },
  license: {
    marginBottom: theme.spacing(1),
  },
  hostGroup: {
    marginLeft: theme.spacing(0.5),
  },
  buttons: {
    marginTop: theme.spacing(1),
    '& > *:not(:first-of-type)': {
      marginLeft: theme.spacing(1),
    },
  },
}));

export const formSchema = Yup.object().shape({
  images: Yup.array().of(
    Yup.mixed(),
  ).required('At least one image is required'),
  license: Yup.string().required('License is required'),
});

type Props = {
  className?: string;
  group?: Group;
  event?: Event;
  onClose?: (...args: unknown[]) => void;
};

const UploadImagesBox = ({ className, group, event, onClose }: Props) => {
  const { classes, cx } = useStyles();
  const { loggedInUser } = useAppContext();

  const [uploadImages] = useMutation(uploadImagesMutation);

  const handleSubmit = useCallback((values) => {
    uploadImages({
      variables: {
        data: {
          ..._pick(values, [
            'images',
            'license',
          ]),
          eventId: _get(event, 'id'),
          groupId: _get(group, 'id'),
        },
      },
      update: (proxy) => {
        // update default license on user
        const meData = proxy.readQuery({
          query: meQuery,
        });
        proxy.writeQuery({
          query: meQuery,
          data: {
            ...meData,
            me: {
              ...meData.me,
              defaultLicense: values.license,
            },
          },
        });
      },
    }).then(() => {
      location.reload();
    });
  }, [event, group, uploadImages]);

  const initialValues = useMemo(() => ({
    images: [],
    license: _get(loggedInUser, 'defaultLicense') || 'by',
  }), [loggedInUser]);

  if (!event && !group) {
    return <p>Must be attached to an event or a group.</p>;
  }

  return (
    <Paper className={cx(classes.paper, className)}>
      <Formik
        initialValues={initialValues}
        validationSchema={formSchema}
        onSubmit={handleSubmit}
      >
        {({ submitCount, isSubmitting }) => {
          const disabled = isSubmitting;
          const forceTouched = submitCount > 0;
          return (
            <Form>
              <DropzoneFileField
                name="images"
                disabled={disabled}
                touched={forceTouched}
                accept={{ 'image/*': [] }}
                placeholder="Drag and drop images or click here"
                className={classes.dropzone}
                maxFiles={10}
                maxSize={50_000_000}
                label="Images (max 10 at a time)"
              />

              <LicenseField
                name="license"
                label="License"
                disabled={disabled}
                touched={forceTouched}
                className={classes.license}
              />

              <div className={classes.buttons}>
                <FormSavingBar message="Uploading images..." />
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={disabled}
                >
                  {isSubmitting ? 'Uploading...' : 'Upload'}
                </Button>
                {onClose && (
                  <Button
                    variant="contained"
                    onClick={onClose}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </Button>
                )}
              </div>
            </Form>
          );
        }}
      </Formik>
    </Paper>
  );
};

export default UploadImagesBox;
